import React, { useMemo } from 'react';
import { StanButton } from '../StanButton/StanButton';
import { StanFilterChip } from './StanFilterChip';
import { StanFilterConfig, StanFiltersProps, StanFilterValue } from './types';

// Filter config and value types (shared with StanFilterChip)

function toInitValue(filter: StanFilterConfig, active?: StanFilterValue[]): StanFilterConfig {
    if (!active || active.length === 0) {
        return { ...filter, initFilterValue: null };
    }

    if (filter.type === 'date') {
        const from = active.find(v => v.operator === 'gte');
        const to = active.find(v => v.operator === 'lte');
        return {
            ...filter,
            initFilterValue: {
                operatorCode: 'bt',
                value: from?.value ? String(from.value) : undefined,
                secondValue: to?.value ? String(to.value) : undefined,
            },
        };
    }

    const selected = active[active.length - 1];
    return {
        ...filter,
        initFilterValue: selected
            ? {
                  operatorCode: selected.operator,
                  value: selected.value,
                  secondValue: selected.secondValue,
              }
            : null,
    };
}

export const StanFilters: React.FC<StanFiltersProps> = ({
    filters,
    onFilterChanged,
    onResetFilters,
    filterValues = [],
    className = '',
    placement = 'bottom',
}) => {
    const hasActive = filterValues.length > 0;

    const filtersWithValues = useMemo(() => {
        // group by id
        const grouped = filterValues.reduce<Record<string, StanFilterValue[]>>((acc, v) => {
            (acc[v.id] ||= []).push(v);
            return acc;
        }, {});

        return filters.map(f => toInitValue(f, grouped[f.id]));
    }, [filters, filterValues]);
    const handleAddFilter = (updates: StanFilterValue[]) => {
        if (!updates?.length) return;
        const id = updates[0]?.id;
        if (!id) return;

        const next = filterValues.filter(f => f.id !== id).concat(updates);
        onFilterChanged(next);
    };

    const handleClearFilter = (id: string) => {
        const next = filterValues.filter(f => f.id !== id);
        onFilterChanged(next);
    };
    return (
        <div className={`flex flex-wrap items-start gap-3 ${className}`}>
            {filtersWithValues.map(f => (
                <StanFilterChip
                    key={`filter-chip-${f.id}`}
                    id={`filter-chip-${f.id}`}
                    label={f.label}
                    filterItem={f}
                    disabled={f.disabled}
                    onApply={handleAddFilter}
                    onClearFilter={handleClearFilter}
                    placement={placement}
                />
            ))}
            {hasActive && (
                <StanButton
                    name='reset-filters'
                    color='secondary'
                    variant='outline'
                    className='!my-0 ml-1'
                    size='sm'
                    onClick={onResetFilters}
                >
                    Reset Filters
                </StanButton>
            )}
        </div>
    );
};
